"use client";

import { useEffect, useState, useMemo } from "react";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import PublicLayout from "@/components/PublicLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { useMetadata } from "@/hooks/useMetadata";
import { FullPageSkeleton } from "@/components/skeletons/FullPageSkeleton";
import {
  CalendarDays,
  Search,
  Filter,
  ShieldCheck,
  MapPin,
  Clock,
  Zap,
  ChevronDown,
  ChevronUp,
  Users,
  Bell,
  BookOpen,
  Star,
  Flame,
  Globe,
  Award,
  Check,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { AppEvent } from "@/lib/api";
import { toast } from "sonner";
import { clsx } from "clsx";
import Link from "next/link";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

export const EVENTS_COPY = {
  title: "Events",
  subtitle: "Authorized Platform Events & Institutional Schedule",
  searchPlaceholder: "Query global events by title or course...",
  filters: {
    all: "All Event Types",
    exam: "Examinations",
    study: "Study Sessions",
    deadline: "Deadlines",
    tutorial: "Tutorials",
  },
  viewButton: "View Event",
  coursePlaceholder: "Filter by Course",
  typePlaceholder: "Filter by Type",
  emptyTitle: "No Scheduled Events",
  emptyDesc: "The global event registry node contains no scheduled operations.",
  eventTypes: {
    exam: "EXAMINATION",
    study: "STUDY SESSION",
    deadline: "DEADLINE",
    tutorial: "TUTORIAL",
  },
  rsvp: "RSVP",
  rsvped: "Attending",
  rsvpFull: "Event Full",
  details: "Details",
  hideDetails: "Hide Details",
  location: "Location",
  datetime: "Date & Time",
  host: "Host",
  attendees: "Attendees",
  description: "Description",
};

const EVENT_TYPE_CONFIG: Record<
  string,
  {
    icon: typeof Flame;
    color: string;
    bg: string;
    border: string;
    label: string;
  }
> = {
  exam: {
    icon: Flame,
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/20",
    label: "EXAMINATION",
  },
  study: {
    icon: BookOpen,
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    label: "STUDY SESSION",
  },
  deadline: {
    icon: Clock,
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    label: "DEADLINE",
  },
  tutorial: {
    icon: Star,
    color: "text-green-400",
    bg: "bg-green-500/10",
    border: "border-green-500/20",
    label: "TUTORIAL",
  },
};

function EventCardSkeleton() {
  return (
    <div className="matte-metal p-8 rounded-[2rem] border border-border space-y-4">
      <div className="flex items-center gap-6">
        <Skeleton className="w-20 h-20 rounded-2xl shrink-0" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-5 w-3/4 rounded-lg" />
          <Skeleton className="h-4 w-1/2 rounded-lg" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
        </div>
      </div>
      <Skeleton className="h-10 w-full rounded-xl" />
    </div>
  );
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function GlobalAcademicEventsNode() {
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<
    "all" | "exam" | "study" | "deadline" | "tutorial"
  >("all");
  const [courses, setCourses] = useState<string[]>([]);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [rsvpEvents, setRsvpEvents] = useState<Set<string>>(new Set());
  const [rsvpDialog, setRsvpDialog] = useState<{
    open: boolean;
    event: AppEvent | null;
  }>({
    open: false,
    event: null,
  });
  const copy = usePageContent("events", EVENTS_COPY);

  useMetadata({ key: "events" });

  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    async function loadEvents() {
      setLoading(true);
      try {
        const [eventsData, courseData] = await Promise.all([
          api.getEvents(),
          api.getCourses(),
        ]);
        const loaded = eventsData || [];
        const hasExamSept = loaded.some(
          (e: any) =>
            (e.type || "") === "exam" &&
            (e.date || "").startsWith("2026-09-09"),
        );
        // Guarantee the first-semester examinations event is visible even if
        // the backend registry has not been seeded yet (launch safety net).
        const examEvent: any = {
          id: "exam-2026-09-09",
          title: "First Semester Examinations Begin",
          type: "exam",
          date: "2026-09-09",
          course: "",
          location: "FUTO, Owerri",
          description:
            "First semester examinations commence across all FUTO departments. Lock in your revision plan and coordinate with your study groups.",
        };
        setEvents(hasExamSept ? loaded : [examEvent, ...loaded]);
        const uniqueCourses = Array.from(
          new Set(
            (courseData || []).map(
              (c: any) => (c as any).code || (c as any).name,
            ),
          ),
        );
        setCourses(uniqueCourses);
      } catch (err) {
        toast.error("Unable to load events. Please try again.");
      } finally {
        setLoading(false);
      }
    }
    loadEvents();
  }, []);

  const filteredEvents = useMemo(() => {
    return events.filter((event: AppEvent) => {
      const matchesSearch = event.title
        ?.toLowerCase()
        .includes(debouncedSearch.toLowerCase());
      const matchesCourse =
        !courseFilter || event.course || "" === courseFilter;
      const matchesType =
        typeFilter === "all" || (event.type || "") === typeFilter;
      return matchesSearch && matchesCourse && matchesType;
    });
  }, [events, debouncedSearch, courseFilter, typeFilter]);

  const toggleRsvp = (eventId: string) => {
    setRsvpEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
        toast.success("RSVP cancelled.");
      } else {
        next.add(eventId);
        toast.success("You're attending! Check your email for details.");
      }
      return next;
    });
  };

  const eventStats = useMemo(() => {
    const total = events.length;
    const byType: Record<string, number> = {};
    events.forEach((e) => {
      const t = e.type || "other";
      byType[t] = (byType[t] || 0) + 1;
    });
    return { total, byType };
  }, [events]);

  if (loading) {
    return (
      <PageTransition>
        <PublicLayout>
          <div className="space-y-12 pb-20">
            <div className="space-y-4">
              <Skeleton className="h-10 w-64 rounded-2xl" />
              <Skeleton className="h-4 w-full rounded-xl" />
            </div>
            <div className="grid gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <EventCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </PublicLayout>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <PublicLayout>
        <div className="space-y-16 pb-24">
          {/* Hero Header */}
          <div className="relative overflow-hidden rounded-[2.5rem] border border-border bg-gradient-to-br from-y/5 via-background to-background p-8 md:p-12">
            <div className="absolute top-0 right-0 w-64 h-64 bg-y/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-y/10 rounded-full blur-2xl" />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-3xl bg-y/10 flex items-center justify-center border border-y/20 shadow-[0_0_30px_rgba(255,214,0,0.15)]">
                  <div
                    className="absolute top-0 left-0 w-6 h-6 md:w-7 md:h-7 bg-y opacity-90"
                    style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
                  />
                  <CalendarDays
                    size={32}
                    className="text-y relative z-10 md:w-10 md:h-10"
                    strokeWidth={2.5}
                  />
                </div>
                <div>
                  <h2 className="display text-3xl md:text-4xl tracking-tighter uppercase">
                    {copy.title}
                  </h2>
                  <p className="text-muted-foreground text-sm uppercase tracking-widest font-medium italic">
                    {copy.subtitle}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="px-4 py-2 rounded-2xl bg-y/10 border border-y/20 text-y">
                  <p className="text-xs font-black uppercase tracking-widest">
                    {eventStats.total} Events
                  </p>
                </div>
                <Link href="/calendar">
                  <button className="bg-y text-black px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:scale-105 transition-all shadow-[0_0_20px_rgba(255,214,0,0.2)]">
                    <CalendarDays size={18} /> Visual_Calendar
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-8">
            {Object.entries(EVENT_TYPE_CONFIG).map(([key, config]) => {
              const Icon = config.icon;
              const count = eventStats.byType[key] || 0;
              return (
                <div
                  key={key}
                  className={clsx(
                    "rounded-2xl border p-4 flex items-center gap-3 transition-all hover:scale-[1.02]",
                    config.bg,
                    config.border,
                  )}
                >
                  <div
                    className={clsx(
                      "w-10 h-10 rounded-xl flex items-center justify-center",
                      config.bg,
                    )}
                  >
                    <Icon size={20} className={config.color} />
                  </div>
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                      {config.label}
                    </p>
                    <p className="text-lg font-black text-foreground">
                      {count}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="sticky top-4 z-20 rounded-3xl border-2 border-y/20 bg-background/90 backdrop-blur-xl p-5 shadow-[0_0_30px_rgba(255,214,0,0.05)]">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1 group">
                <Search
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-y transition-colors"
                  size={18}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={copy.searchPlaceholder}
                  className="w-full bg-card border-2 border-border focus:border-y rounded-2xl py-4 pl-12 pr-4 text-sm font-medium outline-none transition-all placeholder:text-muted-foreground/30 shadow-inner"
                />
              </div>

              <select
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                className="bg-secondary border-2 border-border focus:border-y rounded-2xl px-5 py-4 text-xs font-bold uppercase tracking-widest text-foreground"
              >
                <option value="">{copy.coursePlaceholder}</option>
                {courses.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>

              <select
                value={typeFilter}
                onChange={(e) =>
                  setTypeFilter(
                    e.target.value as
                      | "all"
                      | "exam"
                      | "study"
                      | "deadline"
                      | "tutorial",
                  )
                }
                className="bg-secondary border-2 border-border focus:border-y rounded-2xl px-5 py-4 text-xs font-bold uppercase tracking-widest text-foreground"
              >
                <option value="all">{copy.filters.all}</option>
                <option value="exam">{copy.filters.exam}</option>
                <option value="study">{copy.filters.study}</option>
                <option value="deadline">{copy.filters.deadline}</option>
                <option value="tutorial">{copy.filters.tutorial}</option>
              </select>
            </div>
          </div>

          {/* Event Grid */}
          <div className="grid gap-6">
            <AnimatePresence mode="popLayout">
              {filteredEvents.map((event, i) => {
                const eventType = event.type || "exam";
                const config =
                  EVENT_TYPE_CONFIG[eventType] || EVENT_TYPE_CONFIG.exam;
                const Icon = config.icon;
                const isExpanded = expandedEvent === event.id;
                const isRsvped = rsvpEvents.has(event.id);
                const eventDate = new Date(
                  event.date || event.start_time || "",
                );

                return (
                  <motion.div
                    key={event.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: i * 0.05, duration: 0.3 }}
                    className="group relative rounded-[2rem] border-2 border-border bg-card p-6 md:p-8 shadow-xl hover:border-y/30 transition-all"
                  >
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* Date Block */}
                      <div className="flex lg:flex-col items-center gap-4 lg:gap-2 lg:w-24 shrink-0">
                        <div className="w-16 h-16 md:w-20 md:h-20 bg-secondary rounded-2xl flex flex-col items-center justify-center border border-border shadow-inner">
                          <span className="display text-2xl md:text-3xl text-y">
                            {eventDate.getDate()}
                          </span>
                          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                            {eventDate.toLocaleString("default", {
                              month: "short",
                            })}
                          </span>
                        </div>
                        <div className="lg:hidden flex items-center gap-2 text-xs font-bold text-muted-foreground">
                          <Clock size={12} className="text-y/60" />
                          {eventDate.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 space-y-4">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="space-y-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge
                                variant="outline"
                                className={clsx(
                                  "font-black uppercase tracking-widest text-[10px]",
                                  config.bg,
                                  config.color,
                                  config.border,
                                )}
                              >
                                <Icon size={12} className="mr-1" />
                                {config.label}
                              </Badge>
                              {event.course && (
                                <span className="px-2.5 py-1 rounded-lg bg-y/10 text-y border border-y/20 text-[10px] font-black uppercase tracking-widest">
                                  {event.course}
                                </span>
                              )}
                            </div>
                            <h3 className="font-black text-lg md:text-xl uppercase tracking-tight group-hover:text-y transition-colors">
                              {event.title}
                            </h3>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleRsvp(event.id)}
                              className={clsx(
                                "px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all border-2",
                                isRsvped
                                  ? "bg-y text-black border-y shadow-[0_0_15px_rgba(255,214,0,0.3)]"
                                  : "bg-secondary text-foreground border-border hover:border-y hover:text-y",
                              )}
                            >
                              {isRsvped ? (
                                <>
                                  <Check size={14} className="mr-1" />{" "}
                                  {copy.rsvped}
                                </>
                              ) : (
                                <>
                                  <Users size={14} className="mr-1" />{" "}
                                  {copy.rsvp}
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedEvent(isExpanded ? null : event.id)
                              }
                              className="w-10 h-10 rounded-xl border-2 border-border bg-secondary hover:border-y/40 flex items-center justify-center text-muted-foreground hover:text-y transition-all"
                            >
                              {isExpanded ? (
                                <ChevronUp size={18} />
                              ) : (
                                <ChevronDown size={18} />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-muted-foreground uppercase tracking-widest">
                          <div className="flex items-center gap-2">
                            <MapPin size={12} className="text-y/60" />{" "}
                            {event.location || "VECTRUM CAMP"}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock size={12} className="text-y/60" />{" "}
                            {!event.time &&
                              event.start_time &&
                              new Date(event.start_time).toLocaleTimeString(
                                [],
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                          </div>
                        </div>

                        <AnimatePresence initial={false}>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="pt-4 space-y-4">
                                <div className="rounded-2xl border-2 border-border bg-background/50 p-5 space-y-3">
                                  <div className="flex items-center gap-2 text-y">
                                    <BookOpen size={14} />
                                    <span className="text-[10px] font-black uppercase tracking-widest">
                                      {copy.description}
                                    </span>
                                  </div>
                                  <p className="text-xs text-muted-foreground leading-relaxed">
                                    {event.description ||
                                      "No additional details provided for this event."}
                                  </p>
                                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border/50">
                                    <div>
                                      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                                        {copy.location}
                                      </p>
                                      <p className="text-xs font-bold text-foreground">
                                        {event.location || "VECTRUM CAMP"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                                        {copy.datetime}
                                      </p>
                                      <p className="text-xs font-bold text-foreground">
                                        {eventDate.toLocaleString()}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {filteredEvents.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-24 rounded-[3rem] border-2 border-dashed border-border bg-card/50"
            >
              <div className="w-20 h-20 rounded-full bg-y/10 flex items-center justify-center mx-auto mb-6 border border-y/20">
                <ShieldCheck size={40} className="text-y/60" />
              </div>
              <h4 className="display text-2xl text-muted-foreground uppercase tracking-widest mb-3">
                {copy.emptyTitle}
              </h4>
              <p className="text-xs text-muted-foreground/60 font-bold uppercase tracking-tighter max-w-sm mx-auto">
                {copy.emptyDesc}
              </p>
            </motion.div>
          )}
        </div>
      </PublicLayout>
    </PageTransition>
  );
}
